import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase/config';
import { Restaurant } from '../../types/restaurant';
import { Link } from 'react-router-dom';
import { Building2, Plus, AlertCircle, CheckCircle, Users } from 'lucide-react';

export default function SuperAdminDashboard() {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRestaurants();
  }, []);

  const loadRestaurants = async () => {
    try {
      const q = query(
        collection(db, 'restaurants'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      })) as Restaurant[];
      
      setRestaurants(data);
    } catch (error) {
      console.error('Error loading restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    total: restaurants.length,
    active: restaurants.filter(r => r.active).length,
    inactive: restaurants.filter(r => !r.active).length,
    processors: {
      stripe: restaurants.filter(r => r.processor === 'stripe').length,
      cardnet: restaurants.filter(r => r.processor === 'cardnet').length,
      azul: restaurants.filter(r => r.processor === 'azul').length,
      none: restaurants.filter(r => r.processor === 'none').length,
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando panel de control...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Panel de Control</h1>
          <p className="text-gray-600 mt-1">Gestiona todos los restaurantes y configuraciones del sistema</p>
        </div>
        <Link
          to="/super-admin/create"
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus size={20} />
          Crear restaurante
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total de restaurantes</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Building2 className="text-blue-600" size={40} />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Activo</p>
              <p className="text-3xl font-bold text-green-600">{stats.active}</p>
            </div>
            <CheckCircle className="text-green-600" size={40} />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Inactivo</p>
              <p className="text-3xl font-bold text-gray-400">{stats.inactive}</p>
            </div>
            <AlertCircle className="text-gray-400" size={40} />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Procesadores</p>
              <div className="flex gap-2 mt-2">
                {stats.processors.stripe > 0 && (
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                    Stripe: {stats.processors.stripe}
                  </span>
                )}
                {stats.processors.cardnet > 0 && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    CardNet: {stats.processors.cardnet}
                  </span>
                )}
                {stats.processors.azul > 0 && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                    Azul: {stats.processors.azul}
                  </span>
                )}
              </div>
            </div>
            <Users className="text-blue-600" size={40} />
          </div>
        </div>
      </div>

      {/* Recent Restaurants */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Restaurantes recientes</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Logo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Procesador
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Creado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {restaurants.slice(0, 10).map((restaurant) => (
                <tr key={restaurant.id} className="hover:bg-gray-50">
                  {/* NOMBRE */}
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{restaurant.name}</div>
                    <div className="text-sm text-gray-500">{restaurant.id}</div>
                  </td>
                  
                  {/* LOGO */}
                  <td className="px-6 py-4">
                    {restaurant.logoUrl ? (
                      <img
                        src={restaurant.logoUrl}
                        alt={restaurant.name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg shadow-sm">
                        {restaurant.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </td>
                  
                  {/* PROCESADOR */}
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      restaurant.processor === 'stripe' ? 'bg-purple-100 text-purple-700' :
                      restaurant.processor === 'cardnet' ? 'bg-blue-100 text-blue-700' :
                      restaurant.processor === 'azul' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {restaurant.processor}
                    </span>
                  </td>
                  
                  {/* ESTADO */}
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      restaurant.active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {restaurant.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  
                  {/* CREADO */}
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {restaurant.createdAt?.toLocaleDateString()}
                  </td>
                  
                  {/* ACCIONES */}
                  <td className="px-6 py-4">
                    <Link
                      to={`/super-admin/restaurants/${restaurant.id}`}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      Detalles
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {restaurants.length > 10 && (
          <div className="p-4 border-t border-gray-200 text-center">
            <Link
              to="/super-admin/restaurants"
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Ver todos los restaurantes →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}